import type { MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { URGENCY_LABELS } from '~/lib/problemes-types'
import { ArrowRight, AlertTriangle, Phone, Euro } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: "Problèmes courants — Dépannage artisan | AfficheServices" },
  { name: 'description', content: "Fuite d'eau, panne de chaudière, serrure bloquée, panne électrique — solutions, coûts et artisans disponibles pour chaque problème." },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/problemes' },
  { property: 'og:title', content: 'Problèmes courants — Dépannage artisan | AfficheServices' },
  { property: 'og:description', content: "Solutions pour les problèmes courants : fuite, panne, serrure, électricité." },
  { property: 'og:url', content: 'https://www.afficheservices.fr/problemes' },
]

// Server-only: PROBLEMES array (~28 kB gzip) stays out of the client
// bundle by being imported inside the loader, which RR7 strips from
// the client build. Component reads pre-sorted summaries via useLoaderData.
export async function loader() {
  const { PROBLEMES } = await import('~/lib/problemes-data.server')
  const order: Record<string, number> = { urgent: 0, 'semi-urgent': 1, planifiable: 2 }
  const items = [...PROBLEMES]
    .sort((a, b) => order[a.urgency] - order[b.urgency])
    .map((p) => ({
      slug: p.slug,
      title: p.title,
      urgency: p.urgency,
      shortDesc: p.shortDesc,
      avgCost: p.avgCost,
    }))
  return { items, total: PROBLEMES.length }
}

export default function ProblemesIndexPage() {
  const { items, total } = useLoaderData<typeof loader>()
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Problèmes', url: 'https://www.afficheservices.fr/problemes' },
      ]} />

      <section className="bg-gradient-to-br from-red-900 via-rose-900 to-slate-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-red-200">
            <AlertTriangle className="h-3.5 w-3.5" /> Dépannage
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">Un problème ? On vous aide.</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-red-200">
            {total} problèmes courants : que faire, combien ça coûte, et comment trouver un artisan disponible immédiatement.
          </p>
          <Link
            to="/urgence"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-bold text-red-900 hover:bg-red-50"
          >
            <Phone className="h-4 w-4" /> Urgences 24h
          </Link>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => {
            const urg = URGENCY_LABELS[p.urgency]
            return (
              <Link
                key={p.slug}
                to={`/problemes/${p.slug}`}
                className="group rounded-xl border border-border bg-white p-5 shadow-xs transition-all hover:border-primary hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${urg.color}`}>
                      {urg.label}
                    </span>
                    <h2 className="mt-2 text-sm font-bold text-slate-900 group-hover:text-primary">
                      {p.title}
                    </h2>
                    <p className="mt-1 text-xs text-slate-500 line-clamp-2">{p.shortDesc}</p>
                    <div className="mt-2 flex items-center gap-1 text-xs text-slate-400">
                      <Euro className="h-3 w-3" /> {p.avgCost}
                    </div>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-300 group-hover:text-primary" />
                </div>
              </Link>
            )
          })}
        </div>

        <section className="mt-12 text-center">
          <Link
            to="/devis-gratuit"
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-white hover:bg-primary-hover"
          >
            Recevoir 3 devis gratuits <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  )
}
